/**
 * The invitation card's signal (Claude outputs/community-feed-invite-2026-10-30/
 * DESIGN.md §4.3 to §4.5). Pure state, kept for one visit in sessionStorage;
 * it never leaves the device and is never sent.
 *
 *  read(lesson, kind)   a stream lesson was opened (weight 2) or lingered on (1).
 *                       Each lesson once; an open upgrades a linger and moves it
 *                       to the most recent place; the seeker's own never count.
 *  decide(seam)         the card for the seam after page `seam`, or { card: null, why }.
 *  resolve(card, ask)   Mystic Aida's lessons (Chris 2026-10-30): the search's pick.
 *  place(card)          it goes in; the window starts again after it.
 *  pass(seam)           no card: the seam stays plain for the visit.
 *  spend(seam)          tapped or scrolled past: not drawn again this visit.
 *
 * A visit is this tab until 30 minutes with no activity on the Lessons tab.
 * Every storage access is in try/catch; a storage that fails keeps the state
 * in memory for this mount.
 */
import { ASK_READER } from '@/utils/community-lessons'

export const RULES = {
  LINGER_MS: 3000,        // in view this long, continuously …
  LINGER_RATIO: 0.6,      // … with at least 60% of the card showing
  ACTIVE_MS: 30000,       // and the seeker touched, scrolled, clicked or typed in the last 30 s
  OPEN_W: 2,
  LINGER_W: 1,
  WINDOW: 6,              // the last six read lessons since the last card
  MIN_READS: 3,           // the window holds at least three reads
  MIN_SAME: 3,            // the topic has at least three of them
  SHARE_PCT: 60,          // and at least 60% of the window's weight
  MAX_PER_VISIT: 2,
  VISIT_IDLE_MS: 30 * 60 * 1000,
  SEEN_RATIO: 0.5,
  SEEN_MS: 1000,
  REVEAL_RATIO: 0.4,
  KEEP_READS: 20,
  KEY: 'ma_lesson_invite',
}
export const HOUSE = ASK_READER

const defaultStorage = () => {
  try { return typeof window !== 'undefined' ? window.sessionStorage : null } catch (e) { return null }
}

export function createInvite({ storage = defaultStorage, now = () => Date.now() } = {}) {
  let s = null
  const fresh = () => ({ v: 1, at: now(), seq: 0, reads: [], cards: [], cut: 0, seams: [] })
  const valid = (x) => x && x.v === 1 && Array.isArray(x.reads) && Array.isArray(x.cards)

  function load() {
    try {
      const st = storage()
      if (st) {
        const raw = st.getItem(RULES.KEY)
        let parsed = null
        try { parsed = raw ? JSON.parse(raw) : null } catch (e) { parsed = null }
        s = valid(parsed) ? parsed : (s && raw === null ? s : null)
      }
    } catch (e) { /* private mode: keep what this mount has */ }
    if (!valid(s) || now() - (Number(s.at) || 0) > RULES.VISIT_IDLE_MS) s = fresh()
    if (!Array.isArray(s.seams)) s.seams = []
    return s
  }
  function save() {
    s.at = now()
    try { const st = storage(); if (st) st.setItem(RULES.KEY, JSON.stringify(s)) } catch (e) { /* private mode, full */ }
  }
  function reset() { s = fresh(); save() }
  /** Activity on the Lessons tab keeps the visit (the caller throttles). */
  function touch() { load(); save() }

  function read(lesson, kind) {
    if (!lesson || !lesson.short_uuid || lesson.own) return
    load()
    const w = kind === 'open' ? RULES.OPEN_W : RULES.LINGER_W
    const had = s.reads.find((r) => r.id === lesson.short_uuid)
    if (had && had.w >= w) return
    s.reads = s.reads.filter((r) => r.id !== lesson.short_uuid)
    s.seq = (Number(s.seq) || 0) + 1   // an order, not a clock
    s.reads.push({
      id: lesson.short_uuid,
      topic: lesson.topic || '',
      sub: lesson.reader_subdomain || HOUSE,
      name: lesson.reader_name || '',
      avatar: lesson.reader_avatar || '',
      w,
      t: s.seq,
    })
    s.reads = s.reads.slice(-RULES.KEEP_READS)
    save()
  }
  function windowReads() { load(); return s.reads.filter((r) => r.t > (Number(s.cut) || 0)).slice(-RULES.WINDOW) }
  const used = (sub) => s.cards.some((c) => c.sub === sub)

  function decide(seam) {
    load()
    if (s.cards.length >= RULES.MAX_PER_VISIT) return { card: null, why: 'two cards already this visit' }
    if (s.cards.some((c) => c.seam === seam - 1)) return { card: null, why: 'the last seam had a card' }
    const W = windowReads()
    if (W.length < RULES.MIN_READS) return { card: null, why: `only ${W.length} read since the last card` }
    const total = W.reduce((a, r) => a + r.w, 0)
    const by = {}
    W.forEach((r) => {
      const k = r.topic || '-'
      by[k] = by[k] || { topic: r.topic, n: 0, score: 0, reads: [] }
      by[k].n++
      by[k].score += r.w
      by[k].reads.push(r)
    })
    const ranked = Object.values(by).sort((a, b) => b.score - a.score)
    const top = ranked[0]
    if (ranked[1] && ranked[1].score === top.score) return { card: null, why: 'a tie' }
    if (!top.topic) return { card: null, why: 'mostly lessons without a topic' }
    if (top.n < RULES.MIN_SAME) return { card: null, why: `${top.topic}: only ${top.n} of ${W.length}` }
    if (top.score * 100 < RULES.SHARE_PCT * total) return { card: null, why: `${top.topic}: under 60% of the weight` }
    if (s.cards.some((c) => c.topic === top.topic)) return { card: null, why: `${top.topic} already had its card` }
    let row = 0
    for (let i = W.length - 1; i >= 0 && W[i].topic === top.topic; i--) row++
    const base = { seam, topic: top.topic, row, n: top.n, m: W.length }
    // Every lesson of the topic is Mystic Aida's: resolve() asks the search.
    if (top.reads.every((r) => r.sub === HOUSE)) {
      const r = top.reads[top.reads.length - 1]
      return { card: { ...base, sub: HOUSE, name: r.name || 'Mystic Aida', avatar: r.avatar || '', house: true } }
    }
    const subs = {}
    top.reads.forEach((r) => {
      subs[r.sub] = subs[r.sub] || { sub: r.sub, score: 0, t: 0, name: '', avatar: '' }
      const m = subs[r.sub]
      m.score += r.w
      if (r.t > m.t) { m.t = r.t; m.name = r.name || m.name; m.avatar = r.avatar || m.avatar }
    })
    const mystic = Object.values(subs).sort((a, b) => b.score - a.score || b.t - a.t).find((m) => !used(m.sub))
    if (!mystic) return { card: null, why: 'that Mystic already had a card' }
    return { card: { ...base, sub: mystic.sub, name: mystic.name || mystic.sub, avatar: mystic.avatar, house: false } }
  }

  /**
   * A house card names the search's pick for the topic (the top match who
   * isn't Mystic Aida), so seekers meet other Mystics; else Mystic Aida; a
   * Mystic who already had a card this visit is skipped. Other cards pass.
   */
  function resolve(card, ask) {
    if (!card) return null
    load()
    if (!card.house) return card
    const sub = ask && ask.sub_domain
    if (sub && sub !== HOUSE && !used(sub)) {
      return { ...card, sub, name: ask.name || sub, avatar: ask.avatar || '', house: false, picked: true }
    }
    return used(HOUSE) ? null : card
  }

  function settle(seam) { if (!s.seams.includes(seam)) s.seams.push(seam) }
  function place(card) {
    if (!card) return
    load()
    s.cards.push({ seam: card.seam, topic: card.topic, sub: card.sub, name: card.name || '', avatar: card.avatar || '', row: card.row, n: card.n, m: card.m, spent: false })
    s.cut = Number(s.seq) || 0
    settle(card.seam)
    save()
  }
  function pass(seam) { load(); settle(seam); save() }
  function spend(seam) {
    load()
    const c = s.cards.find((x) => x.seam === seam)
    if (c && !c.spent) { c.spent = true; save() }
  }
  function settled(seam) { load(); return s.seams.includes(seam) }
  function seamCard(seam) { load(); const c = s.cards.find((x) => x.seam === seam); return c ? { ...c } : null }
  function cards() { load(); return s.cards.map((c) => ({ ...c })) }

  return { load, reset, touch, read, windowReads, decide, resolve, place, pass, spend, settled, seamCard, cards }
}
